import { useState } from "react";
import { toast } from "sonner";
import { Search, Download, Trash2, UserSearch } from "lucide-react";
import { searchCustomers, getCustomerRecord, exportCustomer, deleteCustomer } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const when = (iso) => (iso ? new Date(iso).toLocaleString("en-NZ", { day: "numeric", month: "short", year: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—");

export const CustomerLookup = ({ onChanged }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [sel, setSel] = useState(null);
  const [rec, setRec] = useState(null);
  const [busy, setBusy] = useState(false);

  const search = async () => {
    if (q.trim().length < 2) return toast.error("Type at least 2 characters");
    setBusy(true); setSel(null); setRec(null);
    try { setResults(await searchCustomers(q)); } catch { toast.error("Search failed"); } finally { setBusy(false); }
  };
  const open = async (c) => {
    setSel(c); setBusy(true);
    try { setRec(await getCustomerRecord({ email: c.email, phone: c.phone })); } catch { toast.error("Couldn't load record"); } finally { setBusy(false); }
  };
  const doExport = async () => {
    try {
      const blob = await exportCustomer({ email: sel.email, phone: sel.phone });
      const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `echo-customer-${(sel.email || sel.phone).replace("@", "_at_")}.json`; a.click();
      toast.success("Export downloaded"); onChanged?.();
    } catch { toast.error("Export failed"); }
  };
  const doDelete = async () => {
    if (!window.confirm(`Delete everything Echo holds about ${sel.name}? Their name, email, phone and signature are removed from ${rec.summary.bookings} booking(s) and their emails and location history are deleted. This can't be undone.`)) return;
    try {
      const r = await deleteCustomer({ email: sel.email, phone: sel.phone, reason: "Customer request" });
      toast.success(`Deleted — ${r.bookings_anonymised} booking(s) anonymised, ${r.emails_deleted} email(s) removed`);
      setSel(null); setRec(null); setResults(null); setQ(""); onChanged?.();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div className="border border-white/10 rounded-xl p-5" data-testid="customer-lookup">
      <p className="overline text-[#93C5FD] mb-1 flex items-center gap-1"><UserSearch className="w-3.5 h-3.5" /> Customer lookup</p>
      <p className="text-xs text-white/50 mb-3">Find a customer by name, phone or email and see, export or delete everything Echo holds about them.</p>
      <div className="flex gap-2">
        <Input data-testid="customer-search-input" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} placeholder="Name, phone or email" className="bg-white/5 border-white/10 h-11" />
        <Button data-testid="customer-search-btn" onClick={search} disabled={busy} className="h-11 rounded-full bg-[#F5F5F0] text-[#001A4D] hover:bg-white"><Search className="w-4 h-4 mr-1" /> Search</Button>
      </div>
      {results && !sel && (
        <div className="mt-4 space-y-1.5" data-testid="customer-results">
          {results.length === 0 && <p className="text-sm text-white/40" data-testid="customer-no-results">No customers match "{q}".</p>}
          {results.map((c) => (
            <button key={c.key} data-testid={`customer-result-${c.key}`} onClick={() => open(c)} className="w-full text-left rounded-xl border border-white/10 hover:border-[#93C5FD]/50 hover:bg-white/5 p-3 text-sm flex flex-wrap items-center justify-between gap-2">
              <span><span className="font-medium">{c.name}</span> <span className="text-white/50">· {c.email || "no email"} · {c.phone || "no phone"}</span></span>
              <span className="text-xs text-white/40 font-mono">{c.bookings} booking{c.bookings === 1 ? "" : "s"} · last {c.last_hire || "—"}</span>
            </button>
          ))}
        </div>
      )}
      {sel && rec && (
        <div className="mt-4 rounded-xl border border-[#93C5FD]/30 bg-[#2563EB]/5 p-4" data-testid="customer-record">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="font-serif text-2xl" data-testid="customer-record-name">{sel.name}</p>
              <p className="text-sm text-white/60 font-mono">{sel.email || "no email"} · {sel.phone || "no phone"}</p>
              <p className="text-xs text-white/50 mt-2" data-testid="customer-record-summary">{rec.summary.bookings} bookings · {rec.summary.codes} map codes · {rec.summary.lost_alerts} lost alerts · {rec.summary.emails} emails sent · {rec.summary.location_pings} location pings</p>
            </div>
            <div className="flex gap-2">
              <Button data-testid="customer-export-btn" variant="outline" onClick={doExport} className="rounded-full border-white/20 bg-transparent text-white hover:bg-white/10"><Download className="w-4 h-4 mr-1" /> Export</Button>
              <Button data-testid="customer-delete-btn" onClick={doDelete} className="rounded-full bg-red-500/80 hover:bg-red-500 text-white"><Trash2 className="w-4 h-4 mr-1" /> Delete all</Button>
              <Button data-testid="customer-back-btn" variant="ghost" onClick={() => { setSel(null); setRec(null); }} className="rounded-full text-white/70 hover:bg-white/10">Back</Button>
            </div>
          </div>
          <div className="mt-4 space-y-1.5 text-sm max-h-64 overflow-y-auto">
            {rec.bookings.map((b) => (
              <div key={b.id} className="flex flex-wrap justify-between gap-2 border-b border-white/5 pb-1.5" data-testid={`customer-booking-${b.ref}`}>
                <span><span className="font-mono text-[#93C5FD]">{b.ref}</span> · {b.hire_date} {b.start_time} · {b.status}{b.paid ? " · paid" : ""}</span>
                <span className="text-white/50">{(b.bikes || []).map((x) => `${x.qty}× ${x.name}`).join(", ")}{(b.tours || []).length ? ` · ${b.tours.map((t) => t.title || t.route_title).join(", ")}` : ""} · ${b.total}</span>
              </div>
            ))}
            {rec.emails.map((e) => <p key={e.id} className="text-xs text-white/40">Email · {e.subject} · {when(e.created_at)}</p>)}
          </div>
        </div>
      )}
    </div>
  );
};
